import type { LandingContent as SharedLandingContent, Locale } from "@/types";
import aiSummaryPromptEn from "@/prompts/ai-summary.en.txt?raw";
import aiSummaryPromptZh from "@/prompts/ai-summary.zh.txt?raw";

// Homepage copy is pinned to daodaofix9.8; shared marketing copy stays independent.
/**
 * 首页 CTA 的「获取邀请码」弹窗文案：中文放加群二维码（`qrAlt`），英文指向官方 X
 * 账号（`xCta`），两个语言共用同一组去注册 / 去登录入口。只属于首页入口，因此不放进
 * `@/types` 的共享 `LandingContent`，避免其它营销页跟着声明这两条用不到的字段。
 */
export type InvitationDialogCopy = {
  title: string;
  hint: string;
  qrAlt?: string;
  xCta?: string;
  registerCta: string;
  loginCta: string;
  close: string;
};

type LandingContent = Omit<SharedLandingContent, "faq" | "hero"> & {
  hero: SharedLandingContent["hero"] & { invite: InvitationDialogCopy };
  agentShowcaseIntro: {
    eyebrow: string;
    title: string;
    description: string;
  };
  faq: (SharedLandingContent["faq"][number] & { items?: string[] })[];
};

export const DEFAULT_LOCALE: Locale = "en";

export const SUPPORTED_LOCALES: readonly Locale[] = ["en", "zh"];

export const content: Record<Locale, LandingContent> = {
  en: {
    localeName: "English",
    languageToggleLabel: "Switch language",
    nav: [
      { label: "Agents", href: "#agents" },
      { label: "Resources", href: "#workflow" },
      { label: "Pricing", href: "#early-access" },
    ],
    header: {
      earlyAccess: "Request early access",
      menuOpen: "Open navigation",
      menuClose: "Close navigation",
    },
    hero: {
      eyebrow: "Creative intelligence for global commerce",
      slogan: {
        lead: "Know",
        phrases: [
          { text: "What to Create", tracking: "normal" },
          { text: "What to Learn From", tracking: "compact" },
          { text: "What to Write", tracking: "open" },
          { text: "What to Shoot", tracking: "open" },
          { text: "Who to Partner With", tracking: "compact" },
        ],
        tail: "Next.",
        accessibleLabel: "Know What to Create Next.",
      },
      description:
        "Turn your brand context, market signals, and reference videos into clear creative directions, original scripts, and creator matches.",
      brandUrlLabel: "Start with your brand.",
      brandUrlPlaceholder: "yourbrand.com",
      brandUrlError: "Enter a valid brand website.",
      emailLabel: "Work email",
      emailPlaceholder: "you@company.com",
      emailError: "Enter a valid work email.",
      primaryCta: "Request Early Access",
      secondaryCta: "See your next direction.",
      successTitle: "You're on the list",
      successMessage:
        "Thanks. We'll use your brand context to make your first conversation useful.",
      invite: {
        title: "Get an invitation code",
        hint: "Request an invitation code from @ZesrinOfficial on X, then create your account.",
        xCta: "Ask on X / @ZesrinOfficial",
        registerCta: "I have a code — create account",
        loginCta: "Already have an account — log in",
        close: "Close invitation dialog",
      },
    },
    capabilitiesIntro: {
      eyebrow: "From signal to script",
      title: "Stop guessing what to make next",
      description:
        "A connected research and production system gives every creative decision a reason.",
    },
    capabilities: [
      {
        id: "discover",
        step: "01",
        title: "Find relevant videos",
        description:
          "Find relevant videos across platforms and keep the examples connected to your brand question.",
        outcome: "A ranked evidence feed, not another inspiration folder.",
      },
      {
        id: "decode",
        step: "02",
        title: "Decode the pattern",
        description:
          "Understand the hooks, structure, proof, and creative patterns behind each example.",
        outcome: "Creative principles your team can explain and reuse.",
      },
      {
        id: "create",
        step: "03",
        title: "Build an original script",
        description:
          "Turn the evidence into an original direction and script grounded in your brand.",
        outcome: "Work your team can review, assign, and produce immediately.",
      },
    ],
    brandContextIntro: {
      eyebrow: "Brand Context",
      title: "Your brand, understood once. Used everywhere.",
      description:
        "Give every agent the same brand facts, so the research and the output stay specific to you.",
    },
    agentsIntro: {
      eyebrow: "One coordinated team",
      title: "Specialists for each step. One team around your brand.",
      description:
        "The Project Manager organizes the work. Each specialist handles one clear decision and returns a useful deliverable.",
    },
    agentShowcaseIntro: {
      eyebrow: "Specialists for each step. One team around your brand.",
      title: "Meet Your Creative Agent Team",
      description:
        "The Project Manager keeps the work moving. Each specialist owns a key creative decision and delivers a clear, ready-to-use result.",
    },
    workflowIntro: {
      eyebrow: "A traceable creative chain",
      title: "One workflow. Nothing gets lost.",
      description:
        "Move from the first question to work your team can use, with the evidence still attached.",
    },
    useCasesIntro: {
      eyebrow: "Built for teams that ship",
      title: "Who Zesrin is built for.",
      description:
        "For the people turning market signals, brand context, and creative decisions into work.",
    },
    trust: {
      eyebrow: "Early voices",
      title: "From the first people\nto use Zesrin",
      description:
        "A moving record of what early teams notice when research, context, and creative decisions finally stay connected.",
      testimonials: [
        {
          id: "early-voice-01",
          quote: "Preview quote: Zesrin keeps the evidence beside the creative direction, so the reasoning never disappears during handoff.",
          displayName: "Early user placeholder 01",
          descriptor: "Real customer quote to be added",
          avatarKey: "placeholder",
          variant: "feature",
          status: "placeholder",
        },
        {
          id: "early-voice-02",
          quote: "Preview quote: The team can see why an idea exists before debating how to execute it.",
          displayName: "Early user placeholder 02",
          descriptor: "Real customer quote to be added",
          avatarKey: "placeholder",
          variant: "standard",
          status: "placeholder",
        },
        {
          id: "early-voice-03",
          quote: "Preview quote: Brand context feels shared instead of copied into another chat every time.",
          displayName: "Early user placeholder 03",
          descriptor: "Real customer quote to be added",
          avatarKey: "placeholder",
          variant: "compact",
          status: "placeholder",
        },
        {
          id: "early-voice-04",
          quote: "Preview quote: Research, strategy, and writing read like one continuous piece of work.",
          displayName: "Early user placeholder 04",
          descriptor: "Real customer quote to be added",
          avatarKey: "placeholder",
          variant: "standard",
          status: "placeholder",
        },
        {
          id: "early-voice-05",
          quote: "Preview quote: Reviews move faster when every output still carries its source and decision history.",
          displayName: "Early user placeholder 05",
          descriptor: "Real customer quote to be added",
          avatarKey: "placeholder",
          variant: "feature",
          status: "placeholder",
        },
        {
          id: "early-voice-06",
          quote: "Preview quote: It feels less like prompting tools and more like directing a team that remembers the brief.",
          displayName: "Early user placeholder 06",
          descriptor: "Real customer quote to be added",
          avatarKey: "placeholder",
          variant: "compact",
          status: "placeholder",
        },
      ],
    },
    faqIntro: {
      eyebrow: "Questions, answered",
      title: "What to know before you start",
      description: "Zesrin is currently inviting early teams to help shape the product.",
    },
    faq: [
      {
        question: "What is Zesrin?",
        answer:
          "Zesrin is a Social Creative Intelligence workspace for brands and agencies. It brings brand context, market signals, reference videos, creative analysis, script development, and creator research into one connected workflow—so your team can decide what to create next and move from insight to production with less guesswork.",
      },
      {
        question: "Who is Zesrin built for?",
        answer:
          "Zesrin is built for consumer brands, DTC and ecommerce teams, creative and marketing agencies, and the social, content, growth, and production teams behind them. It is especially useful for teams that need to research, produce, and improve short-form video on an ongoing basis.",
      },
      {
        question: "How is Zesrin different from a trend tracker or video summarizer?",
        answer:
          "Trend trackers tell you what is popular. Video summarizers tell you what happened in a video. Zesrin helps you decide what those signals mean for your brand. It connects market evidence with your positioning, products, audience, and creative boundaries to produce focused creative directions, detailed video breakdowns, and brand-ready scripts.",
      },
      {
        question: "What information does Zesrin use?",
        answer:
          "Zesrin works from three kinds of context: your brand and product information, market and competitor signals, and the videos you discover, save, or bring into the workspace. Together, these inputs help the Agents make recommendations that are relevant to your brand—not just the market in general.",
      },
      {
        question: "What can my team create with Zesrin?",
        answer:
          "Depending on the task, Zesrin can help your team produce:",
        items: [
          "Focused creative directions supported by reference videos and market evidence",
          "Detailed video breakdowns covering the hook, narrative, visuals, audio, text, pacing, and CTA",
          "Brand-ready scripts with scene guidance, voiceover, on-screen text, transitions, product proof, and production notes",
          "Creator recommendations and reports based on brand, audience, platform, and content fit",
        ],
      },
      {
        question: "How do the Zesrin Agents work together?",
        answer:
          "The Project Manager understands the request and organizes the work. The Creative Analyst finds and analyzes relevant videos and patterns. The Creative Producer turns those insights into creative directions and production-ready scripts. The Influencer Manager helps identify creators who fit the creative, audience, and market. You can move between Agents while keeping the work organized in one workspace.",
      },
      {
        question: "Can I create from a specific reference video?",
        answer:
          "Yes. You can work from a broader creative direction or study a specific video. Zesrin helps identify the reusable structure and creative logic, then adapts the idea to your brand, product, audience, and constraints. The goal is not to copy the original—it is to learn from what works and create something new for your brand.",
      },
      {
        question: "Does Zesrin replace creative judgment?",
        answer:
          "No. Zesrin gives your team stronger evidence, clearer options, and more production-ready starting points. Your team remains in control of what to approve, change, and produce.",
      },
      {
        question: "Can I save videos, creators, and previous work?",
        answer:
          "Yes. Zesrin includes video and creator libraries for saving useful references and profiles. You can also return to previous conversations and manage reports inside the Agent workspace.",
      },
      {
        question: "Which social platforms does Zesrin support?",
        answer:
          "Zesrin is designed for cross-platform short-form video research and creator discovery. Data availability can vary by platform, region, and workflow. See the Help Center for the latest supported sources and any platform-specific limitations.",
      },
    ],
    finalCta: {
      eyebrow: "CONTINUE IN YOUR WORKSPACE.",
      title: "Bring every creative decision into one place.",
      description:
        "Sign in to keep your brand context, evidence, directions, and deliverables connected.",
      primaryCta: "Request Early Access",
      secondaryCta: "Explore the workflow",
    },
    footer: {
      tagline: "Know what to create next.",
      copyright: "© 2026 Zesrin. All rights reserved.",
      aiSummary: {
        label: "Request AI summary of Zesrin",
        prompt: aiSummaryPromptEn.trim(),
        copied: "Question copied. If the AI does not send it automatically, paste and submit it.",
        popupFallback: "Your browser blocked the new tab. Opening the AI here instead.",
        openWith: "Open the prepared question in",
      },
      groups: [
        {
          label: "Product",
          links: [
            { label: "Agents", href: "/#agents" },
            { label: "Tools", href: "/#workflow" },
            { label: "Pricing", href: "/#early-access" },
          ],
        },
        {
          label: "Use cases",
          links: [
            { label: "Consumer Brands", href: "/use-cases/consumer-brands" },
            { label: "DTC & Ecommerce", href: "/use-cases/dtc-ecommerce" },
            { label: "Agencies", href: "/use-cases/agencies" },
            { label: "Content, Social & Growth", href: "/use-cases/content-social-growth" },
          ],
        },
        {
          label: "Resources",
          links: [
            { label: "About us", href: "/about" },
            { label: "Blog", href: "/blog" },
            { label: "Get help", href: "/help" },
            { label: "Contact us", href: "/contact" },
          ],
        },
        {
          label: "Account",
          links: [
            { label: "Log in", href: "/login" },
          ],
        },
      ],
      legal: [
        { label: "Privacy Policy", href: "/privacy" },
        { label: "Terms of Use", href: "/terms" },
      ],
    },
  },
  zh: {
    localeName: "中文",
    languageToggleLabel: "切换语言",
    nav: [
      { label: "智能体", href: "#agents" },
      { label: "资源", href: "#workflow" },
      { label: "定价", href: "#early-access" },
    ],
    header: {
      earlyAccess: "申请早期体验",
      menuOpen: "打开导航",
      menuClose: "关闭导航",
    },
    hero: {
      eyebrow: "面向全球电商的创意情报系统",
      slogan: {
        lead: "知道下一步该",
        phrases: [
          { text: "创作什么内容", tracking: "normal" },
          { text: "关注哪些信号", tracking: "normal" },
          { text: "筛选哪些视频", tracking: "normal" },
          { text: "拆解什么规律", tracking: "normal" },
          { text: "确定什么方向", tracking: "normal" },
          { text: "写什么脚本", tracking: "normal" },
          { text: "找谁来合作", tracking: "normal" },
        ],
        tail: "",
        accessibleLabel: "知道下一步该关注哪些信号、拆解哪些视频、确定什么方向、写什么脚本，以及与谁合作",
      },
      description:
        "用一支协同 Agent 团队找方向、拆视频、生成符合品牌的脚本，并匹配真正适合执行的 Creator。",
      brandUrlLabel: "品牌网站",
      brandUrlPlaceholder: "yourbrand.com",
      brandUrlError: "请输入有效的品牌网站。",
      emailLabel: "工作邮箱",
      emailPlaceholder: "you@company.com",
      emailError: "请输入有效的工作邮箱。",
      primaryCta: "申请早期体验",
      secondaryCta: "了解工作方式",
      successTitle: "申请已收到",
      successMessage: "感谢你的申请。我们会利用品牌背景，让第一次沟通更有价值。",
      invite: {
        title: "入群获取邀请码",
        hint: "扫码加入 Zesrin 群聊，在群内领取邀请码后即可注册账号。",
        qrAlt: "Zesrin 加群二维码",
        registerCta: "已获取邀请码，去注册",
        loginCta: "已有账号，登录",
        close: "关闭邀请码弹窗",
      },
    },
    capabilitiesIntro: {
      eyebrow: "从信号到脚本",
      title: "不再靠猜测决定下一个创意",
      description: "一套贯通研究与制作的系统，让每个创意决策都有依据。",
    },
    capabilities: [
      {
        id: "discover",
        step: "01",
        title: "发现证据",
        description: "找出值得行动的爆款视频、品类变化、竞品规律和受众信号。",
        outcome: "得到按价值排序的证据流，而不是又一个灵感收藏夹。",
      },
      {
        id: "decode",
        step: "02",
        title: "拆解有效机制",
        description: "逐帧解析钩子、节奏、信任证据、叙事结构、视觉手法与评论反馈。",
        outcome: "沉淀团队能够解释、复用的创意原则。",
      },
      {
        id: "create",
        step: "03",
        title: "生成可拍摄脚本",
        description: "把证据转化为符合品牌的原创概念、脚本、镜头指导和创作者 Brief。",
        outcome: "产出可立即审核、分配并进入制作的内容。",
      },
    ],
    brandContextIntro: {
      eyebrow: "品牌背景",
      title: "让品牌信息成为统一事实来源",
      description:
        "Zesrin 让所有智能体共享持续更新的品牌背景，使研究和产出始终贴合你的业务。",
    },
    agentsIntro: {
      eyebrow: "一支协同团队",
      title: "专业智能体共享同一套品牌背景",
      description:
        "每个智能体负责明确的工作环节，项目经理负责串联任务，并让人工审核始终清晰可见。",
    },
    agentShowcaseIntro: {
      eyebrow: "每一步都有专属 Agent，一支团队始终围绕你的品牌。",
      title: "认识你的创意 Agent 团队",
      description:
        "项目经理推动工作持续向前。每个专业 Agent 负责一项关键创意决策，并交付清晰、可直接使用的成果。",
    },
    workflowIntro: {
      eyebrow: "可追溯的创意链路",
      title: "看清每项产出的依据",
      description: "从宏观市场变化到可制作的营销方案，全程保留支撑决策的证据。",
    },
    useCasesIntro: {
      eyebrow: "为持续交付的团队打造",
      title: "Zesrin 为谁而建",
      description: "为那些把市场信号、品牌背景和创意决策转化为实际工作的人。",
    },
    trust: {
      eyebrow: "早期声音",
      title: "来自第一批\n使用 Zesrin 的人",
      description: "记录早期团队在研究、品牌背景和创意决策真正连接起来之后，最先感受到的变化。",
      testimonials: [
        {
          id: "early-voice-01",
          quote: "示例评价：Zesrin 把证据留在创意方向旁边，交接之后也不会丢掉做出判断的原因。",
          displayName: "内测用户占位 01",
          descriptor: "等待替换为真实客户原话",
          avatarKey: "placeholder",
          variant: "feature",
          status: "placeholder",
        },
        {
          id: "early-voice-02",
          quote: "示例评价：团队可以先看清一个想法为什么成立，再讨论具体怎么执行。",
          displayName: "内测用户占位 02",
          descriptor: "等待替换为真实客户原话",
          avatarKey: "placeholder",
          variant: "standard",
          status: "placeholder",
        },
        {
          id: "early-voice-03",
          quote: "示例评价：品牌背景真正变成了共享信息，不需要每次都复制进新的对话。",
          displayName: "内测用户占位 03",
          descriptor: "等待替换为真实客户原话",
          avatarKey: "placeholder",
          variant: "compact",
          status: "placeholder",
        },
        {
          id: "early-voice-04",
          quote: "示例评价：研究、策略和写作读起来像同一项持续推进的工作。",
          displayName: "内测用户占位 04",
          descriptor: "等待替换为真实客户原话",
          avatarKey: "placeholder",
          variant: "standard",
          status: "placeholder",
        },
        {
          id: "early-voice-05",
          quote: "示例评价：每份产出都带着来源和决策记录，审核速度明显更快。",
          displayName: "内测用户占位 05",
          descriptor: "等待替换为真实客户原话",
          avatarKey: "placeholder",
          variant: "feature",
          status: "placeholder",
        },
        {
          id: "early-voice-06",
          quote: "示例评价：它不像是在轮流提示不同工具，更像是在带领一支记得 Brief 的团队。",
          displayName: "内测用户占位 06",
          descriptor: "等待替换为真实客户原话",
          avatarKey: "placeholder",
          variant: "compact",
          status: "placeholder",
        },
      ],
    },
    faqIntro: {
      eyebrow: "常见问题",
      title: "开始之前，你可能想了解这些",
      description: "Zesrin 正在邀请早期团队与我们共同完善产品。",
    },
    faq: [
      {
        question: "Zesrin 是什么？",
        answer:
          "Zesrin 是一个面向品牌和代理机构的社交创意情报工作空间。它将品牌背景、市场信号、参考视频、创意分析、脚本开发和创作者研究整合到一套连贯的工作流中，让团队更准确地决定下一步创作什么，并以更少的猜测从洞察走向制作。",
      },
      {
        question: "Zesrin 为谁而设计？",
        answer:
          "Zesrin 面向消费品牌、DTC 与电商团队、创意与营销代理机构，以及支撑这些业务的社交媒体、内容、增长和制作团队。它尤其适合需要持续研究、制作并改进短视频的团队。",
      },
      {
        question: "Zesrin 与趋势追踪器或视频摘要工具有何不同？",
        answer:
          "趋势追踪器告诉你什么正在流行，视频摘要工具告诉你视频里发生了什么。Zesrin 则帮助你判断这些信号对品牌意味着什么。它将市场证据与品牌定位、产品、受众和创意边界相结合，生成聚焦的创意方向、详细的视频拆解和可直接用于品牌的脚本。",
      },
      {
        question: "Zesrin 会使用哪些信息？",
        answer:
          "Zesrin 会使用三类背景信息：你的品牌与产品信息、市场与竞争对手信号，以及你在工作空间中发现、保存或导入的视频。这些输入共同帮助智能体给出与你的品牌相关的建议，而不只是针对整个市场的泛化结论。",
      },
      {
        question: "我的团队可以用 Zesrin 创建什么？",
        answer:
          "根据任务不同，Zesrin 可以帮助团队产出：",
        items: [
          "由参考视频和市场证据支撑的聚焦创意方向",
          "覆盖钩子、叙事、画面、音频、文字、节奏和行动号召的详细视频拆解",
          "包含场景指导、旁白、屏幕文字、转场、产品证明和制作备注的品牌可用脚本",
          "根据品牌、受众、平台和内容匹配度生成的创作者推荐与报告",
        ],
      },
      {
        question: "Zesrin 的智能体如何协同工作？",
        answer:
          "项目经理理解需求并组织工作。创意分析师寻找并分析相关视频与模式。创意制作人将这些洞察转化为创意方向和可直接进入制作的脚本。网红合作经理则帮助识别与创意、受众和市场匹配的创作者。你可以在不同智能体之间切换，同时让所有工作都在同一个工作空间内保持有序。",
      },
      {
        question: "我可以基于某个特定的参考视频进行创作吗？",
        answer:
          "可以。你可以从更宽泛的创意方向出发，也可以研究某个特定视频。Zesrin 会识别其中可复用的结构和创意逻辑，再根据你的品牌、产品、受众和限制条件调整这个想法。目标不是复制原作，而是学习它的有效之处，为你的品牌创造新内容。",
      },
      {
        question: "Zesrin 会取代创意判断吗？",
        answer:
          "不会。Zesrin 为团队提供更有力的证据、更清晰的选择和更接近制作阶段的起点。团队始终掌握批准、修改和制作的最终决定权。",
      },
      {
        question: "我可以保存视频、创作者和之前的工作吗？",
        answer:
          "可以。Zesrin 包含视频库和创作者库，用于保存有价值的参考视频与人选资料。你也可以回到之前的对话，并在智能体工作空间中管理报告。",
      },
      {
        question: "Zesrin 支持哪些社交平台？",
        answer:
          "Zesrin 面向跨平台的短视频研究和创作者发现而设计。数据可用性可能因平台、地区和工作流而异。最新支持的数据来源及各平台的具体限制，请查看帮助中心。",
      },
    ],
    finalCta: {
      eyebrow: "在工作台中继续",
      title: "让每个创意决策都在同一个空间发生。",
      description: "登录后，让品牌背景、证据、创意方向和交付成果保持连贯。",
      primaryCta: "申请早期体验",
      secondaryCta: "查看完整流程",
    },
    footer: {
      tagline: "面向全球电商的创意情报系统。",
      copyright: "© 2026 Zesrin. 保留所有权利。",
      aiSummary: {
        label: "请求 AI 总结 Zesrin",
        prompt: aiSummaryPromptZh.trim(),
        copied: "问题已复制。如果 AI 没有自动发送，请粘贴并提交。",
        popupFallback: "浏览器阻止了新标签页，将改为在当前页面打开 AI。",
        openWith: "在以下 AI 中打开准备好的问题：",
      },
      groups: [
        {
          label: "产品",
          links: [
            { label: "智能体", href: "/agents" },
            { label: "工具", href: "/tools" },
            { label: "集成", href: "/integrations" },
          ],
        },
        {
          label: "使用场景",
          links: [
            { label: "消费品牌", href: "/use-cases/consumer-brands" },
            { label: "DTC 与电商", href: "/use-cases/dtc-ecommerce" },
            { label: "代理公司", href: "/use-cases/agencies" },
            { label: "内容、社交与增长", href: "/use-cases/content-social-growth" },
          ],
        },
        {
          label: "资源",
          links: [
            { label: "资源中心", href: "/resources" },
            { label: "博客", href: "/blog" },
            { label: "更新记录", href: "/releases" },
            { label: "帮助中心", href: "/help" },
          ],
        },
        {
          label: "公司",
          links: [
            { label: "关于", href: "/about" },
            { label: "联系", href: "/contact" },
            { label: "价格", href: "/pricing" },
          ],
        },
      ],
      legal: [
        { label: "隐私", href: "/privacy" },
        { label: "条款", href: "/terms" },
      ],
    },
  },
};

export function isLocale(value: string | null | undefined): value is Locale {
  return SUPPORTED_LOCALES.includes(value as Locale);
}

export function getContent(locale: Locale = DEFAULT_LOCALE): LandingContent {
  return content[locale];
}

export function getAlternateLocale(locale: Locale): Locale {
  return locale === "en" ? "zh" : "en";
}
